import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import {
  Page,
  Card,
  BlockStack,
  InlineStack,
  InlineGrid,
  Text,
  Banner,
  Spinner,
  TextField,
  Select,
  Button,
  Divider,
  Link,
  Badge,
} from "@shopify/polaris";
import { DeleteIcon } from "@shopify/polaris-icons";
import AppWrapper from "../AppWrapper";
import supabase from "../../supabase-client";
import { UserAuth } from "../../context/AuthContext";
import { formatPrice, formatDate, formatPlate } from "../../utility/funcs";
import { NEXT_STATUS, TrasladoStatusBadge, formatDuration } from "./trasladoStatus";
import TrasladoTicketsCard from "./TrasladoTicketsCard";
import TrasladoSettlementCard from "./TrasladoSettlementCard";
import { EDITABLE_STATUSES } from "./NewTraslado";
import { quoteComparison } from "./trasladoDiff";
import { tagLabel, tagOptions } from "../../utility/pase";

// One traslado: the frozen quote next to what was actually spent.
// Actual cost = driver_extra (what was agreed with the driver) + traslado_expenses
// (gasolina, casetas, regreso…, the same kinds TrasladoRecovery uses). It is
// written to actual_total when the traslado is settled.

const EXPENSE_KINDS = [
  { label: "Gasolina", value: "gasolina" },
  { label: "Casetas", value: "peaje" },
  { label: "Regreso del chofer", value: "regreso" },
  { label: "Comida", value: "comida" },
  { label: "Verificación", value: "verificacion" },
  { label: "Otro", value: "otro" },
];
const KIND_LABEL = Object.fromEntries(EXPENSE_KINDS.map((k) => [k.value, k.label]));
// Which estimate field each expense kind is compared against.
const ESTIMATE_FIELD = {
  gasolina: "fuel_cost",
  peaje: "tolls",
  regreso: "return_fare",
  comida: "comida",
};

// ISO timestamp -> value for <input type="datetime-local"> in local time.
const toLocalInput = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

function Traslado() {
  const { trasladoId } = useParams();
  const { hasPermission } = UserAuth();
  const canEdit = hasPermission("services.create");

  const [order, setOrder] = useState(undefined);
  const [expenses, setExpenses] = useState([]);
  const [tickets, setTickets] = useState([]);
  const [settlements, setSettlements] = useState([]);
  const [tags, setTags] = useState([]);

  useEffect(() => {
    supabase
      .from("pase_tags")
      .select("*")
      .order("name")
      .then(({ data }) => setTags(data || []));
  }, []);
  const [reloadKey, setReloadKey] = useState(0);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);

  const [driverExtra, setDriverExtra] = useState("");
  // Real trip, entered by hand from the fleet GPS software.
  const [startedAt, setStartedAt] = useState("");
  const [finishedAt, setFinishedAt] = useState("");
  const [actualKm, setActualKm] = useState("");
  const [newKind, setNewKind] = useState("gasolina");
  const [newAmount, setNewAmount] = useState("");
  const [newNotes, setNewNotes] = useState("");

  const load = async () => {
    const { data, error: err } = await supabase
      .from("traslado_orders")
      .select(
        "*, car(id, plate, vin, year, model(name, image_url, brand(name))), driver:service_providers(id, name, phone), trasladista:trasladistas!traslado_orders_trasladista_fkey(id, name, phone), secondary:trasladistas!traslado_orders_trasladista_secondary_fkey(id, name, phone)",
      )
      .eq("id", trasladoId)
      .single();
    if (err) {
      setError(err.message);
      setOrder(null);
      return;
    }
    setOrder(data);
    setDriverExtra(data.driver_extra != null ? String(data.driver_extra) : "");
    setStartedAt(toLocalInput(data.started_at));
    setFinishedAt(toLocalInput(data.finished_at));
    setActualKm(data.actual_km != null ? String(data.actual_km) : "");
    const [{ data: exp }, { data: tks }, { data: sts }] = await Promise.all([
      supabase.from("traslado_expenses").select("*, cfdi:expense_cfdis(uuid), pase:pase_movements(id)").eq("traslado_order", trasladoId).order("created_at"),
      supabase.from("traslado_tickets").select("*").eq("traslado_order", trasladoId).order("created_at"),
      supabase.from("traslado_settlements").select("*").eq("traslado_order", trasladoId).order("created_at"),
    ]);
    setExpenses(exp || []);
    setTickets(tks || []);
    setSettlements(sts || []);
    setReloadKey((k) => k + 1);
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [trasladoId]);

  const update = async (patch) => {
    setBusy(true);
    setError(null);
    const { error: err } = await supabase
      .from("traslado_orders")
      .update(patch)
      .eq("id", trasladoId);
    setBusy(false);
    if (err) setError(err.message);
    else load();
  };

  // Real cost = agreed chofer pay + comida allowance + approved trasladista
  // tickets + expenses GoMove paid directly (manual traslado_expenses).
  const expenseTotal = expenses.reduce((s, e) => s + Number(e.amount || 0), 0);
  const approvedTickets = tickets.filter((t) => t.status === "approved");
  const ticketTotal = approvedTickets.reduce((s, t) => s + Number(t.total || 0), 0);
  // Pagos adicionales after the anticipo add to the agreed chofer pay / comida.
  const extras = settlements.filter((s) => s.extra_reason && s.status !== "cancelled");
  const extraChofer = extras.reduce((s, x) => s + Number(x.amount_chofer || 0), 0);
  const extraComida = extras.reduce((s, x) => s + Number(x.amount_comida || 0), 0);
  const choferActual = Number(driverExtra || 0) + extraChofer;
  const comidaAllowance = Number(order?.estimate?.comida || 0) + extraComida;
  const actual = choferActual + comidaAllowance + ticketTotal + expenseTotal;

  // Marking en camino / entregado stamps the time if it's still empty; staff
  // can correct it afterwards in "Recorrido real".
  const changeStatus = (to) =>
    update({
      status: to,
      ...(to === "in_progress" && !order.started_at ? { started_at: new Date().toISOString() } : {}),
      ...(to === "completed" && !order.finished_at ? { finished_at: new Date().toISOString() } : {}),
    });

  // Delivered and fully paid -> Liquidado, which takes it out of Activos. The
  // RPC refuses while a ticket or a payment to the trasladista is pending.
  const closeTraslado = async () => {
    setBusy(true);
    const { error: err } = await supabase.rpc("close_traslado", { p_order: Number(trasladoId) });
    setBusy(false);
    if (err) setError(err.message);
    else load();
  };

  const saveTrip = () =>
    update({
      started_at: startedAt ? new Date(startedAt).toISOString() : null,
      finished_at: finishedAt ? new Date(finishedAt).toISOString() : null,
      actual_km: actualKm === "" ? null : Math.round(Number(actualKm)),
    });
  // After the anticipo, chofer pay and comida are locked (DB trigger too).
  const advancePaid = settlements.some((s) => s.status !== "cancelled");

  const addExpense = async () => {
    const amount = Number(newAmount);
    if (!(amount > 0)) return;
    setBusy(true);
    const { error: err } = await supabase.from("traslado_expenses").insert({
      traslado_order: Number(trasladoId),
      kind: newKind,
      amount,
      notes: newNotes.trim() || null,
      source: "manual",
    });
    setBusy(false);
    if (err) {
      setError(err.message);
      return;
    }
    setNewAmount("");
    setNewNotes("");
    load();
  };

  const removeExpense = async (id) => {
    setBusy(true);
    const { error: err } = await supabase.from("traslado_expenses").delete().eq("id", id);
    setBusy(false);
    if (err) setError(err.message);
    else load();
  };

  if (order === undefined)
    return (
      <AppWrapper>
        <Page title={`Traslado #${trasladoId}`} backAction={{ url: "/vehicle_transfers" }}>
          <Spinner size="small" />
        </Page>
      </AppWrapper>
    );
  if (order === null)
    return (
      <AppWrapper>
        <Page title={`Traslado #${trasladoId}`} backAction={{ url: "/vehicle_transfers" }}>
          <Banner tone="critical">{error || "No encontrado"}</Banner>
        </Page>
      </AppWrapper>
    );

  const e = order.estimate || {};
  const open = !["settled", "cancelled", "historical"].includes(order.status);
  const carTitle = [order.car?.model?.brand?.name, order.car?.model?.name, order.car?.year]
    .filter(Boolean)
    .join(" ");
  const spentByKind = (kind) =>
    kind === "comida"
      ? comidaAllowance + expenses.filter((x) => x.kind === "comida").reduce((s, x) => s + Number(x.amount), 0)
      : expenses.filter((x) => x.kind === kind).reduce((s, x) => s + Number(x.amount), 0) +
        approvedTickets.filter((t) => t.kind === kind).reduce((s, t) => s + Number(t.total), 0);

  const compareRow = (label, est, act) => (
    <InlineGrid columns={3} gap="200" key={label}>
      <Text>{label}</Text>
      <Text alignment="end" tone="subdued">
        {est != null ? formatPrice(est) : "—"}
      </Text>
      <Text alignment="end">{act ? formatPrice(act) : "—"}</Text>
    </InlineGrid>
  );

  const actions = open && canEdit
    ? [
        ...(NEXT_STATUS[order.status] || []).map((n) => ({
          content: n.label,
          onAction: () => changeStatus(n.to),
          disabled: busy,
        })),
        ...(order.status === "completed" && hasPermission("services.pay")
          ? [{ content: "Cerrar traslado", onAction: closeTraslado, disabled: busy }]
          : []),
      ]
    : [];

  return (
    <AppWrapper>
      <Page
        title={`Traslado #${order.id}`}
        titleMetadata={
          <InlineStack gap="200">
            <TrasladoStatusBadge status={order.status} />
            {order.cobro_cliente && <Badge tone="info">Con cobro a cliente</Badge>}
          </InlineStack>
        }
        subtitle={`${order.origin_label || order.origin_cp || "?"} → ${
          order.dest_label || order.dest_cp || "?"
        }`}
        backAction={{ url: "/vehicle_transfers" }}
        primaryAction={actions[0]}
        secondaryActions={[
          ...(canEdit && EDITABLE_STATUSES.includes(order.status) && !advancePaid
            ? [{ content: "Editar", url: `/vehicle_transfers/${order.id}/edit` }]
            : []),
          ...actions.slice(1),
          ...(open && canEdit
            ? [
                {
                  content: "Cancelar traslado",
                  destructive: true,
                  disabled: busy,
                  onAction: () => changeStatus("cancelled"),
                },
              ]
            : []),
        ]}
      >
        <BlockStack gap="400">
          {error && (
            <Banner tone="critical" onDismiss={() => setError(null)}>
              {error}
            </Banner>
          )}
          <InlineGrid columns={{ xs: 1, md: 2 }} gap="400">
            <Card>
              <BlockStack gap="300">
                <Text variant="headingMd">Unidad y ruta</Text>
                <InlineStack gap="300" blockAlign="center" wrap={false}>
                  {order.car?.model?.image_url && (
                    <img
                      src={order.car.model.image_url}
                      alt=""
                      style={{ width: 90, height: 60, objectFit: "contain" }}
                    />
                  )}
                  <BlockStack gap="050">
                    <Link url={`/cars/${order.car?.id}`}>{formatPlate(order.car?.plate)}</Link>
                    <Text tone="subdued">{carTitle}</Text>
                  </BlockStack>
                </InlineStack>
                <Divider />
                <InlineGrid columns={2} gap="200">
                  <Text tone="subdued">Origen</Text>
                  <Text>
                    {order.origin_label || "—"} {order.origin_cp && `(CP ${order.origin_cp})`}
                  </Text>
                  <Text tone="subdued">Destino</Text>
                  <Text>
                    {order.dest_label || "—"} {order.dest_cp && `(CP ${order.dest_cp})`}
                  </Text>
                  <Text tone="subdued">Distancia</Text>
                  <Text>
                    {order.planned_km ? `${order.planned_km} km` : "—"}
                    {e.route?.duration_min ? ` · ${formatDuration(e.route.duration_min)}` : ""}
                  </Text>
                  <Text tone="subdued">Programado</Text>
                  <Text>{order.scheduled_at ? formatDate(order.scheduled_at) : "—"}</Text>
                  {/* Editable at any stage: Casetas TAG uses it to link the tolls. */}
                  <Text tone="subdued">TAG PASE</Text>
                  {canEdit ? (
                    <Select
                      label="TAG PASE"
                      labelHidden
                      options={tagOptions(tags)}
                      value={order.pase_tag || ""}
                      onChange={(v) => update({ pase_tag: v || null })}
                      disabled={busy}
                    />
                  ) : (
                    <Text>{tagLabel(tags.find((t) => t.tag === order.pase_tag)) || "—"}</Text>
                  )}
                  <Text tone="subdued">Trasladista</Text>
                  <Text>
                    {order.trasladista ? (
                      <Link url="/trasladistas">{order.trasladista.name}</Link>
                    ) : (
                      "—"
                    )}
                  </Text>
                  {order.secondary && (
                    <>
                      <Text tone="subdued">Secundario</Text>
                      <Text>
                        <Link url="/trasladistas">{order.secondary.name}</Link>
                        {` · ${formatPrice(order.secondary_amount || 0)} del pago al chofer`}
                      </Text>
                    </>
                  )}
                  {/* Historical traslados were invoiced by a provider (broker). */}
                  {order.driver && (
                    <>
                      <Text tone="subdued">Proveedor (factura)</Text>
                      <Link url={`/service_providers/${order.driver.id}`}>{order.driver.name}</Link>
                    </>
                  )}
                  {order.service && (
                    <>
                      <Text tone="subdued">Servicio</Text>
                      <Link url={`/services/${order.service}`}>#{order.service}</Link>
                    </>
                  )}
                </InlineGrid>
                {order.notes && (
                  <>
                    <Divider />
                    <Text>{order.notes}</Text>
                  </>
                )}
              </BlockStack>
            </Card>

            <Card>
              <BlockStack gap="300">
                <Text variant="headingMd">Estimado vs real</Text>
                <InlineGrid columns={3} gap="200">
                  <Text tone="subdued" variant="bodySm">
                    Concepto
                  </Text>
                  <Text tone="subdued" variant="bodySm" alignment="end">
                    Estimado
                  </Text>
                  <Text tone="subdued" variant="bodySm" alignment="end">
                    Real
                  </Text>
                </InlineGrid>
                {compareRow("Chofer", order.suggested_extra, choferActual)}
                {Object.entries(ESTIMATE_FIELD).map(([kind, field]) =>
                  compareRow(KIND_LABEL[kind], e[field], spentByKind(kind)),
                )}
                {["verificacion", "otro"]
                  .filter((k) => spentByKind(k) > 0)
                  .map((k) => compareRow(KIND_LABEL[k], null, spentByKind(k)))}
                <Divider />
                <InlineGrid columns={3} gap="200">
                  <Text fontWeight="bold">Total</Text>
                  <Text alignment="end" fontWeight="bold" tone="subdued">
                    {order.estimated_total != null ? formatPrice(order.estimated_total) : "—"}
                  </Text>
                  <Text alignment="end" fontWeight="bold">
                    {formatPrice(order.status === "settled" ? order.actual_total : actual)}
                  </Text>
                </InlineGrid>
                {order.cobro_cliente && order.precio_cliente != null && (() => {
                  const cost = order.status === "settled" ? Number(order.actual_total) : actual;
                  const margin = Number(order.precio_cliente) - cost;
                  return (
                    <>
                      <Divider />
                      <InlineGrid columns={3} gap="200">
                        <Text>Precio al cliente</Text>
                        <Text />
                        <Text alignment="end">{formatPrice(order.precio_cliente)}</Text>
                        <Text fontWeight="semibold">Margen</Text>
                        <Text alignment="end" tone="subdued">
                          {order.estimated_total != null
                            ? formatPrice(Number(order.precio_cliente) - Number(order.estimated_total))
                            : "—"}
                        </Text>
                        {/* Real margin only once the trip's costs are in. */}
                        {["completed", "settled"].includes(order.status) ? (
                          <Text alignment="end" fontWeight="semibold" tone={margin < 0 ? "critical" : "success"}>
                            {formatPrice(margin)}
                          </Text>
                        ) : (
                          <Text alignment="end" tone="subdued">—</Text>
                        )}
                      </InlineGrid>
                    </>
                  );
                })()}
                {e.complete === false && (
                  <Text tone="subdued" variant="bodySm">
                    La cotización estaba incompleta; el estimado es un mínimo.
                  </Text>
                )}
              </BlockStack>
            </Card>
          </InlineGrid>

          {(() => {
            // Only what was changed by hand; no card at all when nothing was.
            const cmp = quoteComparison(order.estimate, order.driver_extra);
            if (!cmp?.adjusted) return null;
            const changed = cmp.rows.filter((r) => r.key !== "total" && r.changed);
            const total = cmp.rows.find((r) => r.key === "total");
            const signed = (v) => `${v > 0 ? "+" : ""}${formatPrice(v)}`;
            return (
              <Card>
                <BlockStack gap="200">
                  <Text variant="headingMd">Cambios vs sugerido</Text>
                  {changed.map((r) => (
                    <InlineStack key={r.key} align="space-between">
                      <Text>
                        {r.label}{" "}
                        <Text as="span" tone="subdued">
                          ({formatPrice(r.suggested)} → {formatPrice(r.quoted)})
                        </Text>
                      </Text>
                      <Text tone={r.diff > 0 ? "critical" : "success"}>{signed(r.diff)}</Text>
                    </InlineStack>
                  ))}
                  {total?.changed && (
                    <InlineStack align="space-between">
                      <Text fontWeight="semibold">Total</Text>
                      <Text fontWeight="semibold" tone={total.diff > 0 ? "critical" : "success"}>
                        {signed(total.diff)}
                      </Text>
                    </InlineStack>
                  )}
                </BlockStack>
              </Card>
            );
          })()}

          <Card>
            <BlockStack gap="300">
              <Text variant="headingMd">Recorrido real</Text>
              <InlineGrid columns={{ xs: 1, md: 3 }} gap="300">
                <TextField
                  label="Inicio"
                  type="datetime-local"
                  autoComplete="off"
                  value={startedAt}
                  onChange={setStartedAt}
                  disabled={!canEdit}
                />
                <TextField
                  label="Fin"
                  type="datetime-local"
                  autoComplete="off"
                  value={finishedAt}
                  onChange={setFinishedAt}
                  disabled={!canEdit}
                  error={
                    startedAt && finishedAt && new Date(finishedAt) < new Date(startedAt)
                      ? "El fin no puede ser antes del inicio"
                      : undefined
                  }
                />
                <TextField
                  label="Km recorridos (GPS)"
                  type="number"
                  suffix="km"
                  autoComplete="off"
                  value={actualKm}
                  onChange={setActualKm}
                  disabled={!canEdit}
                  helpText={(() => {
                    if (actualKm === "" || !order.planned_km) return null;
                    const diff = Math.round(Number(actualKm)) - order.planned_km;
                    const pct = Math.round((diff / order.planned_km) * 100);
                    return `Cotizado ${order.planned_km} km · ${diff >= 0 ? "+" : ""}${diff} km (${pct >= 0 ? "+" : ""}${pct}%)`;
                  })()}
                />
              </InlineGrid>
              {startedAt && finishedAt && new Date(finishedAt) >= new Date(startedAt) && (
                <Text tone="subdued" variant="bodySm">
                  {`Duración: ${formatDuration(Math.round((new Date(finishedAt) - new Date(startedAt)) / 60000))}${
                    e.route?.duration_min ? ` · Google estimaba ${formatDuration(e.route.duration_min)}` : ""
                  }`}
                </Text>
              )}
              {canEdit &&
                (toLocalInput(order.started_at) !== startedAt ||
                  toLocalInput(order.finished_at) !== finishedAt ||
                  String(order.actual_km ?? "") !== actualKm) && (
                  <InlineStack>
                    <Button
                      loading={busy}
                      disabled={!!(startedAt && finishedAt && new Date(finishedAt) < new Date(startedAt))}
                      onClick={saveTrip}
                    >
                      Guardar recorrido
                    </Button>
                  </InlineStack>
                )}
            </BlockStack>
          </Card>

          <TrasladoSettlementCard
            order={order}
            tickets={tickets}
            settlements={settlements}
            canPay={hasPermission("services.pay")}
            onChange={load}
          />

          <TrasladoTicketsCard
            order={order}
            canEdit={canEdit}
            canReview={hasPermission("services.pay")}
            reloadKey={reloadKey}
            onChange={load}
          />

          <Card>
            <BlockStack gap="300">
              <Text variant="headingMd">Pago al chofer y gastos pagados por GoMove</Text>
              <InlineStack gap="300" blockAlign="end">
                <div style={{ width: 200 }}>
                  <TextField
                    label="Pago acordado al chofer"
                    type="number"
                    prefix="$"
                    autoComplete="off"
                    value={driverExtra}
                    onChange={setDriverExtra}
                    disabled={!open || !canEdit || advancePaid}
                    helpText={[
                      order.suggested_extra != null && `Tarifa: ${formatPrice(order.suggested_extra)}`,
                      order.secondary &&
                        `${order.trasladista?.name || "Principal"} recibe ${formatPrice(
                          Number(driverExtra || 0) - Number(order.secondary_amount || 0),
                        )} · ${order.secondary.name} ${formatPrice(order.secondary_amount || 0)}`,
                    ]
                      .filter(Boolean)
                      .join(" · ")}
                  />
                </div>
                {open && canEdit && !advancePaid && Number(driverExtra) !== Number(order.driver_extra) && (
                  <Button
                    loading={busy}
                    onClick={() =>
                      update({
                        driver_extra: driverExtra === "" ? null : Number(driverExtra),
                      })
                    }
                  >
                    Guardar pago
                  </Button>
                )}
              </InlineStack>

              {expenses.length === 0 ? (
                <Text tone="subdued">Sin gastos registrados.</Text>
              ) : (
                <BlockStack gap="100">
                  {expenses.map((x) => (
                    <InlineStack key={x.id} align="space-between" blockAlign="center">
                      <Text>
                        {KIND_LABEL[x.kind] || x.kind}
                        {x.notes ? ` · ${x.notes}` : ""}
                      </Text>
                      <InlineStack gap="200" blockAlign="center">
                        {/* TAG PASE tolls: PASE invoices each tag monthly, so they count as invoiced. */}
                        {x.source === "manual" &&
                          (x.cfdi?.length ? (
                            <Badge tone="success">Con factura</Badge>
                          ) : x.pase?.length ? (
                            <Badge tone="success">Facturada (TAG)</Badge>
                          ) : (
                            <Badge>Sin factura</Badge>
                          ))}
                        <Text>{formatPrice(x.amount)}</Text>
                        {open && canEdit && !x.cfdi?.length && (
                          <Button
                            variant="plain"
                            tone="critical"
                            icon={DeleteIcon}
                            accessibilityLabel="Eliminar gasto"
                            onClick={() => removeExpense(x.id)}
                          />
                        )}
                      </InlineStack>
                    </InlineStack>
                  ))}
                </BlockStack>
              )}

              {open && canEdit && (
                <InlineStack gap="200" blockAlign="end">
                  <div style={{ width: 170 }}>
                    <Select
                      label="Gasto"
                      options={EXPENSE_KINDS}
                      value={newKind}
                      onChange={setNewKind}
                    />
                  </div>
                  <div style={{ width: 140 }}>
                    <TextField
                      label="Monto"
                      type="number"
                      prefix="$"
                      autoComplete="off"
                      value={newAmount}
                      onChange={setNewAmount}
                    />
                  </div>
                  <div style={{ flexGrow: 1, minWidth: 160 }}>
                    <TextField
                      label="Nota"
                      autoComplete="off"
                      value={newNotes}
                      onChange={setNewNotes}
                    />
                  </div>
                  <Button disabled={!(Number(newAmount) > 0)} loading={busy} onClick={addExpense}>
                    Agregar
                  </Button>
                </InlineStack>
              )}
            </BlockStack>
          </Card>
        </BlockStack>
      </Page>
    </AppWrapper>
  );
}

export default Traslado;
